import React, { Fragment, useEffect, useState } from 'react';
import { Button, Col, Container, Label, Row, Table } from 'reactstrap';
import { toast } from "react-toastify";
import { NumberField, ServiceThreadSpinner } from '.';
import { api } from '../utils';


export default function ManageQuoteDefaults() {
   const [model, setDefaults] = useState({});
   const [spin, setSpinner] = useState(true);
   const disabled = JSON.stringify(model) === "{}";

   useEffect(() => {
      GetQuoteDefaults();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   function GetQuoteDefaults() {
      setSpinner(true);
      api
         .fetch(`Engineering/GetQuoteDefaults`)
         .then((response) => {
            if (response?.data) {
               if (response.data.success) {
                  setDefaults(response.data.defaults);
               } else {
                  toast.error(response.data.message);
               }
            }
            else {
               toast.error("Unable to retrieve default values!");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setSpinner(false);
         });
   }

   function onChange(field, value) {
      let changes = Object.assign({}, model);
      changes[field] = value;
      setDefaults(changes);
   }

   function SaveDefaults() {
      if (disabled) {
         toast.error("Missing Defaults!");
         return;
      }

      if (model?.fiberCost < 0) {
         toast.warn("Fiber Cost is required");
         return;
      }
      if (model?.finishCost < 0) {
         toast.warn("Finish Cost is required");
         return;
      }
      if (model?.lotSize < 0) {
         toast.warn("Lot Size is required");
         return;
      }
      if (model?.markupPct <= 0) {
         toast.warn("Markup % is required");
         return;
      }
      if (model?.minCharge < 0) {
         toast.warn("Minimum Tier1 Cost is required");
         return;
      }
      if (model?.minQty < 0) {
         toast.warn("Minimum Tier1 Qty is required");
         return;
      }
      if (model?.pkgCost < 0) {
         toast.warn("Packaging Cost is required");
         return;
      }
      if (model?.qaCost < 0) {
         toast.warn("QA Cost is required");
         return;
      }
      if (model?.scrapPct < 0) {
         toast.warn("Scrap % is required");
         return;
      }
      if (model?.surcharge < 0) {
         toast.warn("Surcharges are required");
         return;
      }
      if (model?.lotSize < model?.minQty) {
         toast.warn("Lot Size cannot be less than the Tier 1 Min Qty");
         return;
      }
      if (model?.tier2QtyMult < 1) {
         toast.warn("Tier 2 Qty Multipler is required and cannot be less than 1");
         return;
      }
      if (model?.tier2PricePct < 50) {
         toast.warn("Tier 2 Price % is required and cannot be less than 50%");
         return;
      }
      if (model?.tier3QtyMult < 1) {
         toast.warn("Tier 3 Qty Multipler is required and cannot be less than 1");
         return;
      }
      if (model?.tier3PricePct < 50) {
         toast.warn("Tier 3 Price % is required and cannot be less than 50%");
         return;
      }

      setSpinner(true);
      api.post(`Engineering/SaveQuoteDefaults`, model)
         .then((response) => {
            if (response.data.success) {
               toast.success("Price Quote Generator defaults updated!")
            } else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            setSpinner(false);
         })
         .finally(() => {
            setSpinner(false);
         });
   }

   return (
      <Container fluid>
         <Row>
            <Col>
               <Fragment>
                  <ServiceThreadSpinner on={spin}>
                     <Row className="px-2 py-2">
                        <Col xxl={{ offset: 0, size: 12 }}>
                           <Label className="noWrap h5">Change one or more default values below and click the Save Defaults button</Label>
                           <br /><br />
                           <Label className="noWrap h5">Materials ($/lb)</Label>
                           <Table className="transparent-background my-0">
                              <tbody>
                                 <tr>
                                    <td className="transparent-background data-cell noWrap h6 width185px">
                                       Fiber ($/lb):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.fiberCost || "0"}
                                          onChange={(v) => onChange("fiberCost", v)}
                                          min={0.0}
                                          step={0.01}
                                          decimals={2}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background width70px"></td>
                                    <td className="transparent-background data-cell noWrap h6 width150px">
                                       Finish / Lube ($/lb):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.finishCost || "0"}
                                          onChange={(v) => onChange("finishCost", v)}
                                          min={0.0}
                                          step={0.01}
                                          decimals={2}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background width70px"></td>
                                    <td className="transparent-background data-cell noWrap h6 width150px">
                                       Packaging ($/lb):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.pkgCost || "0"}
                                          onChange={(v) => onChange("pkgCost", v)}
                                          min={0.0}
                                          step={0.01}
                                          decimals={2}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background"></td>
                                 </tr>
                              </tbody>
                           </Table>
                           <br /><br />
                           <Label className="noWrap h5">QA, Pricing & Lot</Label>
                           <Table className="transparent-background my-0">
                              <tbody>
                                 <tr>
                                    <td className="transparent-background data-cell noWrap h6 width185px">
                                       QA ($/lb):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.qaCost || "0"}
                                          onChange={(v) => onChange("qaCost", v)}
                                          min={0.0}
                                          step={0.01}
                                          decimals={2}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background width70px"></td>
                                    <td className="transparent-background data-cell noWrap h6 width150px">
                                       Surcharges ($/lb):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.surcharge || "0"}
                                          onChange={(v) => onChange("surcharge", v)}
                                          min={0.0}
                                          step={0.05}
                                          decimals={2}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background width70px"></td>
                                    <td className="transparent-background data-cell noWrap h6 width150px">
                                       Markup (%):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.markupPct || "0"}
                                          onChange={(v) => onChange("markupPct", v)}
                                          min={0}
                                          max={200}
                                          step={1}
                                          decimals={0}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background"></td>
                                 </tr>
                                 <tr>
                                    <td className="transparent-background data-cell noWrap h6 width150px">
                                       Lot Size (lb):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.lotSize || "0"}
                                          onChange={(v) => onChange("lotSize", v)}
                                          min={model?.minQty ?? 0}
                                          step={100}
                                          decimals={0}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background width70px"></td>
                                    <td className="transparent-background data-cell noWrap h6 width150px">
                                       Scrap (%):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.scrapPct || "0"}
                                          onChange={(v) => onChange("scrapPct", v)}
                                          min={0}
                                          max={15}
                                          step={0.5}
                                          decimals={1}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td colSpan={4} className="transparent-background"></td>
                                 </tr>
                              </tbody>
                           </Table>
                           <br /><br />
                           <Label className="noWrap h5">Tier Requirements</Label>
                           <Table className="transparent-background my-0">
                              <tbody>
                                 <tr>
                                    <td className="transparent-background data-cell noWrap h6 width185px">
                                       Min Total Charge ($):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.minCharge || "0"}
                                          onChange={(v) => onChange("minCharge", v)}
                                          min={100}
                                          step={1}
                                          decimals={0}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background width70px"></td>
                                    <td className="transparent-background data-cell noWrap h6 width150px">
                                       Min Qty (lb):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.minQty || "0"}
                                          onChange={(v) => onChange("minQty", v)}
                                          min={100}
                                          step={1}
                                          decimals={0}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background width70px"></td>
                                    <td colSpan={2} className="transparent-background"></td>
                                 </tr>
                                 <tr>
                                    <td className="transparent-background data-cell noWrap h6 width185px">
                                       Tier 2 Pricing (%):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.tier2PricePct || "0"}
                                          onChange={(v) => onChange("tier2PricePct", v)}
                                          min={model?.tier3PricePct ?? 50}
                                          max={100}
                                          step={1}
                                          decimals={0}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background width70px"></td>
                                    <td className="transparent-background data-cell noWrap h6 width150px">
                                       Tier 2 Qty (x T1):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.tier2QtyMult || "0"}
                                          onChange={(v) => onChange("tier2QtyMult", v)}
                                          min={1}
                                          max={model?.tier3QtyMult ?? 100}
                                          step={0.01}
                                          decimals={2}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background width70px"></td>
                                    <td colSpan={2} className="transparent-background"></td>
                                 </tr>
                                 <tr>
                                    <td className="transparent-background data-cell noWrap h6 width185px">
                                       Tier 3 Pricing (%):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.tier3PricePct || "0"}
                                          onChange={(v) => onChange("tier3PricePct", v)}
                                          min={50}
                                          max={model?.tier2PricePct ?? 100}
                                          step={1}
                                          decimals={0}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background width70px"></td>
                                    <td className="transparent-background data-cell noWrap h6 width150px">
                                       Tier 3 Qty (x T1):
                                    </td>
                                    <td className="transparent-background width135px">
                                       <NumberField
                                          value={model?.tier3QtyMult || "0"}
                                          onChange={(v) => onChange("tier3QtyMult", v)}
                                          min={model?.tier2QtyMult ?? 1}
                                          step={0.01}
                                          decimals={2}
                                          className="h-100 py-1 no-spinner"   // `w-36 md:w-44 lg:w-52 ${className}`
                                       />
                                    </td>
                                    <td className="transparent-background width70px"></td>
                                    <td colSpan={2} className="transparent-background">
                                       <Button title="Save"
                                          className="shredButtonExcel ms-2"
                                          onClick={() => SaveDefaults()}
                                          disabled={disabled}>
                                          Save Defaults
                                       </Button>
                                    </td>
                                 </tr>
                              </tbody>
                           </Table>
                        </Col>
                     </Row>
                  </ServiceThreadSpinner>
               </Fragment>
            </Col>
         </Row>
      </Container>
   );
}
